import { SectionFolder } from '@src/components/SectionFolder';
import { SectionTitle } from '@src/components/SectionTitle';
import { SectionProps } from '../types/Settings';
import { LocalizedStrings } from '@src/utils/localizedStrings';
import { FORM_LINE_TITLE_SIZE, SECTION_TITLE_SIZE } from '../utils/variables';
import { SectionContent } from '../subcomponents/SectionContent';
import { createChangeSettings } from '../utils/createChangeSettings';
import { ThumbnailIcon } from '../../Icons/DropDownIcons/Thumbnail';
import { ThumbnailShapeSelector } from '../selectors/ThumbnailShapeSelector';
import { SettingsCheckbox } from '../subcomponents/SettingsCheckbox';
import { WidgetView } from 'common';
import { ThumbnailCornersSelector } from '../selectors/ThumbnailCornersSelector';
import { FormLine } from '@src/components/Form/FormLine';
import { CheckboxNote } from '../subcomponents/CheckboxNote';
import { ADALONG_COLORS } from '@src/styles';
import { ThumbnailProductCTASelector } from '../selectors/ThumbnailProductCTASelector';
import { Tooltip } from '@mui/material';
import { InfoSvgIcon } from '@src/components/SvgIcons/Info';
import { style } from 'typestyle';

const ThumbnailDesign = ({ opened, onOpen, editedWidget, setEditedWidget }: SectionProps) => {
  const changeSettings = createChangeSettings(editedWidget, setEditedWidget);

  const handleThumbnailChange = <K extends keyof WidgetView['settings']>(
    key: K,
    value: WidgetView['settings'][K],
  ) => {
    if (value === editedWidget.settings[key]) {
      return;
    }
    changeSettings({ [key]: value });
  };

  const isMosaicOrWall = ['mosaic', 'wall'].includes(editedWidget.settings.type);

  return (
    <SectionFolder
      title={
        <SectionTitle size={SECTION_TITLE_SIZE}>
          <ThumbnailIcon />
          {lang.title.main}
        </SectionTitle>
      }
      opened={opened}
      afterTitle={
        <Tooltip title={lang.tooltips.thumbnail} placement="top">
          <span className={infoThumbnailDesignClass}>
            <InfoSvgIcon />
          </span>
        </Tooltip>
      }
      onOpen={onOpen}
    >
      <SectionContent>
        <FormLine title={lang.title.creator} size={FORM_LINE_TITLE_SIZE}>
          <SettingsCheckbox
            label={lang.fields.displayName}
            checked={editedWidget.settings.display_name}
            onClick={() =>
              changeSettings({
                display_name: !editedWidget.settings.display_name,
              })
            }
          />
          
          {isMosaicOrWall && editedWidget.settings.display_name ? (
            <CheckboxNote marginBottom={8} fontSize={11} color={ADALONG_COLORS.LIGHT_GRAY}>
              {lang.notes.creatorNameMobile}
            </CheckboxNote>
          ) : null }

          {editedWidget.settings.display_name ? (
            <SettingsCheckbox
              label={lang.fields.usernameLink}
              checked={editedWidget.settings.username_link}
              onClick={() =>
                changeSettings({
                  username_link: !editedWidget.settings.username_link,
                })
              }
            />
          ) : null }
        </FormLine>

        <FormLine title={lang.title.shape} size={FORM_LINE_TITLE_SIZE}>
          <ThumbnailShapeSelector
            thumbnail_shape={editedWidget.settings.thumbnail_shape}
            onChange={(value) => handleThumbnailChange('thumbnail_shape', value)}
          />
        </FormLine>

        <FormLine title={lang.title.corners} size={FORM_LINE_TITLE_SIZE}>
          <ThumbnailCornersSelector
            thumbnail_corners={editedWidget.settings.thumbnail_corners}
            onChange={(value) => handleThumbnailChange('thumbnail_corners', value)}
          />
        </FormLine>

        <FormLine title={lang.title.productCTA} size={FORM_LINE_TITLE_SIZE}>
          <ThumbnailProductCTASelector
            thumbnail_product_cta={editedWidget.settings.thumbnail_product_cta}
            onChange={(value) => handleThumbnailChange('thumbnail_product_cta', value)}
          />
        </FormLine>

        <FormLine title={lang.title.videos} size={FORM_LINE_TITLE_SIZE}>
          <SettingsCheckbox
            label={lang.fields.autoplayOnHover}
            checked={editedWidget.settings.thumbnail_video_autoplay_hover}
            onClick={() =>
              changeSettings({
                thumbnail_video_autoplay_hover: !editedWidget.settings.thumbnail_video_autoplay_hover,
              })
            }
          />
        </FormLine>
      </SectionContent>
    </SectionFolder>
  );
};

const lang = LocalizedStrings({
  en: {
    title: {
      main: 'Thumbnail Design',
      shape: 'shape',
      corners: 'corners',
      productCTA: 'Product CTA',
      creator: 'Post creator info',
      videos: 'Videos',
    },
    tooltips: {
      thumbnail: 'Customize the appearance of your UGC thumbnails including shape, corners, and creator information display',
    },
    fields: { 
      displayName: 'Display creator name',
      usernameLink: 'Enable link to original post',
      autoplayOnHover: 'Autoplay videos on hover',
    },
    notes: {
      creatorNameMobile: '*In mobile mode, creator name is not visible in Mosaic and Wall layouts',
    },
  },
});

const infoThumbnailDesignClass = style({
  marginLeft: 10,
  color: ADALONG_COLORS.LIGHT_GRAY,
});

export default ThumbnailDesign;
